import { type ReactNode, useEffect, useImperativeHandle, useRef } from 'react'

import { cn } from '@/lib/utils'
import { type ShadowBlockEntry, stampShadowBlocks } from './content-brief-shadow-blocks.helper'
import markerCss from './content-brief-shadow-markers.css?raw'

export interface ContentBriefShadowRendererProps {
  htmlContent: string
  hostRef?: React.Ref<HTMLDivElement>
  onBlocksReady: (blockList: ShadowBlockEntry[]) => void
  className?: string
}

const buildShadowContent = (host: HTMLDivElement, htmlContent: string): ShadowBlockEntry[] => {
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
  shadow.replaceChildren()

  const parsed = new DOMParser().parseFromString(htmlContent, 'text/html')

  parsed.head.querySelectorAll('style').forEach((styleEl) => {
    shadow.appendChild(styleEl.cloneNode(true))
  })

  const markerStyle = document.createElement('style')
  markerStyle.textContent = markerCss
  shadow.appendChild(markerStyle)

  const container = document.createElement('div')
  container.className = 'master-report-content'
  while (parsed.body.firstChild) {
    container.appendChild(parsed.body.firstChild)
  }
  shadow.appendChild(container)

  return stampShadowBlocks(container)
}

export const ContentBriefShadowRenderer: React.FC<ContentBriefShadowRendererProps> = ({
  htmlContent,
  hostRef,
  onBlocksReady,
  className,
}): ReactNode => {
  const localHostRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(hostRef, () => localHostRef.current as HTMLDivElement, [])

  useEffect(() => {
    const host = localHostRef.current
    if (!host) return
    const blockList = buildShadowContent(host, htmlContent)
    onBlocksReady(blockList)
  }, [htmlContent, onBlocksReady])

  return (
    <div
      ref={localHostRef}
      className={cn(
        'content-brief-shadow-host h-full w-full overflow-auto bg-background',
        className
      )}
    />
  )
}
